import type { input } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { TextField } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation } from 'react-router';
import { DialogType, StyleObj } from '../../../@types';
import { MarketType, OutcomeType } from '../../../@types/api';
import { QUERY_KEYS } from '../../../constants';
import { useModal } from '../../../contexts/ModalContext';
import useMutateData from '../../../hooks/useMutateData';
import { OutcomeTypeFormData, outcomeTypeFormSchema } from '../../../schema';
import FormFieldStack from '../../atoms/FormFieldStack';
import Switch from '../../atoms/Switch';
import FormModalLayout from '../../layouts/FormModalLayout';
import FormNumberInput from '../../molecules/FormNumberInput';
import SportSelect from '../../molecules/SportSelect';
import FormAutocomplete from '../FormAutocomplete';

const DEFAULT_OUTCOME_TYPE_FORM_DATA = {
  sportId: undefined,
  marketTypeId: undefined,
  name: '',
  shortName: null,
  description: null,
  position: null,
  isActive: true,
};

const styles: StyleObj = {
  position: {
    width: 80,
  },
};

type Props = DialogType;

const OutcomeTypeForm = ({ closeModal }: Props) => {
  const { item } = useModal<OutcomeType>();
  const { state } = useLocation();

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<input<typeof outcomeTypeFormSchema>, unknown, OutcomeTypeFormData>({
    defaultValues: DEFAULT_OUTCOME_TYPE_FORM_DATA,
    resolver: zodResolver(outcomeTypeFormSchema),
  });

  const sportId = watch('sportId');

  useEffect(() => {
    if (item) {
      reset({
        ...item,
        sportId: item.sport.id,
        marketTypeId: item.marketType.id,
      });
    } else {
      reset({
        ...DEFAULT_OUTCOME_TYPE_FORM_DATA,
        ...state?.defaultValues,
      });
    }
  }, [state?.defaultValues, item, reset]);

  const { createData, updateData } = useMutateData('outcome-types', [QUERY_KEYS.outcomeTypes]);

  const handleClose = () => {
    closeModal?.();
    reset(DEFAULT_OUTCOME_TYPE_FORM_DATA);
  };

  const onFormSubmit = (newOutcome: OutcomeTypeFormData) => {
    if (item) {
      updateData(item.id, newOutcome, () => {
        handleClose();
      });
    } else {
      createData(newOutcome, () => {
        handleClose();
      });
    }
  };

  const isEdit = !!item;

  return (
    <FormModalLayout
      onSave={handleSubmit(onFormSubmit)}
      label={isEdit ? 'Edit outcome' : 'Add new outcome'}
      onClose={handleClose}
      isEdit={isEdit}
      editEntity='outcome-types'
    >
      <SportSelect control={control} name='sportId' required error={errors.sportId} disabled={isEdit} />
      <FormAutocomplete<MarketType, OutcomeTypeFormData>
        name='marketTypeId'
        control={control}
        label='Market'
        url='market-types'
        queryKey={[QUERY_KEYS.marketTypes, sportId]}
        queryParams={{
          isActive: true,
          marketTypeIds: [item?.marketType.id],
          sportId,
        }}
        getOptionLabel={(options, value) => {
          const option = options.find((option) => option?.id === value);
          return option ? `${option?.name}, ${option?.eventPartName || ''}` : '';
        }}
        disabled={isEdit}
        error={errors.marketTypeId}
        required
      />
      <TextField
        error={!!errors.name}
        helperText={errors.name?.message}
        label='Name'
        {...register('name')}
        required
        disabled={isEdit}
      />
      <TextField
        error={!!errors.shortName}
        helperText={errors.shortName?.message}
        label='Short name'
        {...register('shortName')}
      />
      <TextField
        error={!!errors.description}
        helperText={errors.description?.message}
        label='Description'
        multiline
        {...register('description')}
      />
      <FormFieldStack label='Position'>
        <FormNumberInput name='position' control={control} error={errors.position} sx={styles.position} />
      </FormFieldStack>
      <FormFieldStack label='Status'>
        <Controller name='isActive' control={control} render={({ field }) => <Switch {...field} ref={null} />} />
      </FormFieldStack>
    </FormModalLayout>
  );
};

export default OutcomeTypeForm;
